'use client'

import { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import Script from 'next/script'

/** Whether `pathname` is one of `pages`. An entry ending in `/*` also covers everything under it. */
export function onChatPage(pathname: string, pages: readonly string[]): boolean {
  const path = pathname.replace(/\/+$/, '') || '/'
  return pages.some((page) =>
    page.endsWith('/*') ? path.startsWith(page.slice(0, -1)) : path === page
  )
}

/**
 * Rock'n's website chat. The script and the widget frame come from Chat itself (chat.rocknearthworks.com.au); no token means no widget, so
 * only the environments whose env file sets one show it. Both are loaded, not linked: no href here needs outbound().
 *
 * Only on `pages` (home, contact and the pages that sell or support). The site navigates without reloading, so once the script is in, the
 * bubble is hidden, and any open window closed, on every other page rather than left floating over a fun one like /doof.
 */
export function ChatWidget({
  baseUrl,
  websiteToken,
  pages,
}: {
  baseUrl: string
  websiteToken: string
  pages: readonly string[]
}) {
  const here = onChatPage(usePathname(), pages)
  // Loaded the first time someone reaches a chat page, and kept from then on.
  const [loaded, setLoaded] = useState(here)
  if (here && !loaded) setLoaded(true)

  const hereRef = useRef(here)
  useEffect(() => {
    hereRef.current = here
    const apply = () => {
      const chat = window.$chatwoot
      if (!chat) return
      if (!hereRef.current) chat.toggle('close')
      chat.toggleBubbleVisibility(hereRef.current ? 'show' : 'hide')
    }
    apply()
    // The widget can finish loading after a move to a page it shouldn't show on.
    window.addEventListener('chatwoot:ready', apply)
    return () => window.removeEventListener('chatwoot:ready', apply)
  }, [here])

  if (!websiteToken || !loaded) return null
  return (
    <Script
      src={`${baseUrl}/packs/js/sdk.js`}
      strategy="lazyOnload"
      onLoad={() => {
        window.chatwootSettings = { position: 'right', type: 'standard', locale: 'en_AU' }
        window.chatwootSDK?.run({ websiteToken, baseUrl })
      }}
    />
  )
}
